// app/javascript/controllers/chart_controller.js
import {Controller} from "@hotwired/stimulus"
import Chart from 'chart.js/auto'

export default class extends Controller {
    static targets = ["canvas", "labels", "datasets", "title"]

    connect() {
        if (this.hasCanvasTarget) {
            this.initializeChart()

            // Chart.js sizes the canvas from its container the moment the chart
            // is created. During a cross-page Turbo visit — especially inside the
            // Hotwire Native webview, which can report a stale width before the
            // native layout settles — the canvas gets locked to a too-wide size
            // and spills the page horizontally. (A pull-to-refresh is a same-URL
            // morph that re-renders against the settled layout, which is why it
            // "fixes itself.") Re-measure once the browser has laid out the new
            // page so the canvas matches its container.
            this.resizeChart = () => this.chart?.resize()
            this.rafId = requestAnimationFrame(this.resizeChart)
            document.addEventListener("turbo:load", this.resizeChart)

            // Listen for changes on the period selector if this is a bar chart
            if (this.data.get("type") === "bar") {
                const selector = document.getElementById('revenue_period')
                if (selector) {
                    this.boundUpdatePeriod = this.updatePeriod.bind(this)
                    this.periodSelector = selector
                    selector.addEventListener('change', this.boundUpdatePeriod)
                }
            }
        }
    }

    disconnect() {
        if (this.rafId) cancelAnimationFrame(this.rafId)
        if (this.resizeChart) document.removeEventListener("turbo:load", this.resizeChart)
        if (this.periodSelector && this.boundUpdatePeriod) {
            this.periodSelector.removeEventListener('change', this.boundUpdatePeriod)
        }
        this.chart?.destroy()
    }

    updatePeriod(event) {
        if (this.chart) {
            const period = event.target.value;

            // Get the appropriate labels for the selected period from the HTML
            const labelAttributeName = {
                'month': 'monthLabels',
                'quarter': 'quarterLabels',
                'annual': 'yearLabels'
            }[period];

            // Parse the labels from the data attribute
            const newLabels = JSON.parse(this.labelsTarget.dataset[labelAttributeName] || '[]');

            // Update the chart labels
            this.chart.data.labels = newLabels;

            // Access the correct datasets for the selected period
            const datasetAttributeName = {
                'month': 'monthDatasets',
                'quarter': 'quarterDatasets',
                'annual': 'yearDatasets'
            }[period];

            const periodDatasets = JSON.parse(this.datasetsTarget.dataset[datasetAttributeName] || '[]');
            if (periodDatasets.length > 0) {
                this.chart.data.datasets = this.resolveDatasetColors(periodDatasets);
            }

            this.chart.update();
        }
    }

    // Chart.js passes color strings straight to Canvas 2D, which silently
    // rejects "var(--color-foo)" and falls back to black. Resolve CSS vars
    // to computed color strings here so dataset palettes can stay token-driven
    // in ERB while still rendering correctly.
    resolveDatasetColors(datasets) {
        return datasets.map((dataset) => {
            const out = { ...dataset };
            for (const key of ['backgroundColor', 'borderColor']) {
                const value = dataset[key];
                if (Array.isArray(value)) {
                    out[key] = value.map((color) => this.resolveCssVar(color));
                } else if (typeof value === 'string') {
                    out[key] = this.resolveCssVar(value);
                }
            }
            return out;
        });
    }

    resolveCssVar(value) {
        if (typeof value !== 'string') return value;
        const match = value.match(/^\s*var\((--[a-zA-Z0-9-_]+)\)\s*$/);
        if (!match) return value;
        const resolved = getComputedStyle(this.element).getPropertyValue(match[1]).trim();
        return resolved || value;
    }

    initializeChart() {
        const chartType = this.data.get("type") || "bar";
        const title = this.hasTitleTarget ? this.titleTarget.textContent : "";
        const labels = JSON.parse(this.labelsTarget.dataset.labels || '[]');

        // Get datasets configuration
        let datasets;
        if (this.hasDatasetsTarget) {
            datasets = this.resolveDatasetColors(JSON.parse(this.datasetsTarget.dataset.datasets || '[]'));
        } else {
            // Fallback for backward compatibility
            console.warn("Chart is missing datasets target. Please update your HTML.");
            datasets = [{
                label: title,
                data: [0, 0, 0], // Default empty data
                backgroundColor: ["#4F46E5", "#10B981", "#F59E0B"],
            }];
        }

        // Configure chart options based on chart type
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: chartType === 'doughnut' ? 'right' : 'top',
                    labels: {
                        usePointStyle: chartType === 'doughnut' ? true : false,
                        padding: 15
                    }
                },
                title: {
                    display: false
                }
            }
        };

        // Add specific options for bar charts
        if (chartType === 'bar') {
            options.scales = {
                x: {
                    grid: {
                        display: false
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: {
                        drawBorder: false
                    }
                }
            };
        }

        // For doughnut charts, we need to add some additional options
        if (chartType === 'doughnut') {
            options.cutout = '65%';
            options.plugins.doughnutLabel = {
                labels: [
                    {
                        text: 'Total',
                        font: {
                            size: '16'
                        }
                    },
                    {
                        text: () => {
                            // Calculate total from dataset
                            return datasets[0].data.reduce((a, b) => a + b, 0);
                        },
                        font: {
                            size: '24',
                            weight: 'bold'
                        }
                    }
                ]
            };
        }

        // Create chart configuration
        const config = {
            type: chartType,
            data: {
                labels: labels,
                datasets: datasets
            },
            options: options
        };

        // Initialize chart
        this.chart = new Chart(this.canvasTarget, config);
    }
}